/**
 * IELTS back office (D-105): defensive readers for the IELTS blocks of the API (format details,
 * course tree, lessons, exercises, progress in bands). Every rule comes from the versioned
 * format table (`GET academy/format/?exam=ielts_general`); nothing about the exam is written here.
 */

import { letter } from "@agencyos/core";

import { isObject, num, str } from "./shared";

export const IELTS_SKILLS = ["co", "ce", "ee", "eo"] as const;
export type IeltsSkill = (typeof IELTS_SKILLS)[number];

const nullable = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);

/**
 * Bands: how they are written and the server's rounding rule are shared with the learner space and
 * the phone (@agencyos/core); `bandText(value, language)` keeps its name in the back office.
 */
export { HALF_BAND, bandSteps, criteriaBand, formatBand as bandText, roundBand, roundingOf } from "@agencyos/core";

// --- Course tree ------------------------------------------------------------------------------

export type LessonStatus = "draft" | "reviewed" | "published" | "archived";

export interface UnitNode {
  id: string;
  code: string;
  title: unknown;
  skills: string[];
  counts: Partial<Record<LessonStatus, number>>;
}

export function unitsOf(value: unknown): UnitNode[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter(isObject).map((u) => ({
    id: str(u.id) || str(u.code),
    code: str(u.code),
    title: u.title,
    skills: Array.isArray(u.skills) ? (u.skills as unknown[]).map(str).filter(Boolean) : [],
    counts: isObject(u.lessons) ? (Object.fromEntries(Object.entries(u.lessons).map(([k, v]) => [k, num(v)])) as UnitNode["counts"]) : {},
  }));
}

/** Lessons per status over units (the course header and the key figures). */
export function sumCounts(units: UnitNode[]): Record<LessonStatus, number> {
  const out: Record<LessonStatus, number> = { draft: 0, reviewed: 0, published: 0, archived: 0 };
  for (const unit of units) for (const [status, n] of Object.entries(unit.counts)) if (status in out) out[status as LessonStatus] += n ?? 0;
  return out;
}

/** Title of a course level from the format table (`details.course.levels`), else the code. */
export function levelLabels(details: unknown): Record<string, unknown> {
  const course = isObject(details) && isObject(details.course) ? details.course : {};
  const levels = Array.isArray(course.levels) ? (course.levels as unknown[]).filter(isObject) : [];
  return Object.fromEntries(levels.map((l) => [str(l.key), l.label]));
}

// --- Lesson content ---------------------------------------------------------------------------

export interface VocabularyItem {
  word: string;
  definition: string;
  example: string;
  fr: string;
  /** TCF: the English translation (D-107). */
  en: string;
}

export function vocabularyOf(value: unknown): VocabularyItem[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter(isObject).flatMap((v) => {
    const word = str(v.word);
    return word ? [{ word, definition: str(v.definition), example: str(v.example), fr: str(v.fr), en: str(v.en) }] : [];
  });
}

/** Objectives: a list of strings, or {en: [...], fr: [...]} per language. */
export function objectivesOf(value: unknown, language: string): string[] {
  if (Array.isArray(value)) return (value as unknown[]).map(str).filter(Boolean);
  if (isObject(value)) {
    const lang = language.slice(0, 2);
    return objectivesOf(value[lang] ?? value.en ?? value.fr, language);
  }
  return [];
}

/** The explanation of a lesson in one language ({en, fr} or a plain text). */
export function textIn(value: unknown, language: "en" | "fr"): string {
  if (typeof value === "string") return language === "en" ? value : "";
  return isObject(value) ? str(value[language]) : "";
}

export interface ExerciseView {
  id: string;
  type: string;
  prompt: string;
  options: string[];
  left: string[];
  right: string[];
  items: string[];
  text: string;
  script: string;
  choose: number;
  maxWords: number;
  minWords: number;
  seconds: number;
  blanks: number;
  answer: unknown;
  explanation: string;
  helpFr: string;
  modelAnswer: string;
}

const list = (value: unknown): string[] => (Array.isArray(value) ? (value as unknown[]).map(str) : []);

/** An exercise as the learner sees it (options, pairs, blanks, text or script), plus its key. */
export function exerciseView(value: unknown): ExerciseView {
  const e = isObject(value) ? value : {};
  const data = isObject(e.data) ? e.data : {};
  const prompt = str(e.prompt) || str(data.prompt);
  return {
    id: str(e.id),
    type: str(e.type),
    prompt,
    options: list(data.options ?? e.options),
    left: list(data.left ?? e.left),
    right: list(data.right ?? e.right),
    items: list(data.items ?? e.items),
    text: str(data.text ?? e.text),
    script: str(data.script ?? e.script),
    choose: num(data.choose ?? e.choose),
    maxWords: num(data.max_words ?? e.max_words),
    minWords: num(data.min_words ?? e.min_words),
    seconds: num(data.seconds ?? e.seconds),
    blanks: (prompt.match(/_{3,}/g) ?? []).length,
    answer: e.answer,
    explanation: str(e.explanation),
    helpFr: str(e.help_fr),
    modelAnswer: str(e.model_answer),
  };
}

/** The expected answer, readable by a trainer (« B », « am / is », « FALSE », « 1 → b »…). */
export function answerText(view: ExerciseView): string {
  const mark = (i: unknown) => (typeof i === "number" ? letter(i) : str(i));
  const a = view.answer;
  if (a === null || a === undefined) return "";
  switch (view.type) {
    case "mcq":
      return mark(a);
    case "multi":
      return Array.isArray(a) ? (a as unknown[]).map(mark).join(", ") : mark(a);
    case "gap":
      return Array.isArray(a) ? (a as unknown[]).map((g) => (Array.isArray(g) ? (g as unknown[]).map(str).join(" / ") : str(g))).join(" · ") : str(a);
    case "short":
      return Array.isArray(a) ? (a as unknown[]).map(str).join(" / ") : str(a);
    case "match":
      return Array.isArray(a) ? (a as unknown[]).map((r, i) => `${i + 1} → ${view.right[num(r)] ?? mark(r)}`).join(" · ") : "";
    case "order":
      return Array.isArray(a) ? (a as unknown[]).map((i) => view.items[num(i)] ?? str(i)).join(" → ") : "";
    default:
      return typeof a === "string" || typeof a === "number" ? str(a) : "";
  }
}

// --- Learner progress in bands ------------------------------------------------------------------

export interface BandSkill {
  skill: IeltsSkill;
  band: number | null;
  clb: number | null;
  gap: number | null;
  trend: string;
}

export interface BandSitting {
  attempt: string;
  kind: string;
  mock: number | null;
  at: string;
  overall: number | null;
  skills: Partial<Record<IeltsSkill, { band: number | null; clb: number | null; cefr: string }>>;
}

export interface BandProgress {
  target: number | null;
  overall: number | null;
  verdict: "ready" | "almost" | "not_ready";
  skills: BandSkill[];
  evolution: BandSitting[];
  critical: { code: string; message: string }[];
  daysLeft: number | null;
  rank: { platform: { position: number; total: number; topPercent: number } | null; agency: { position: number; total: number; topPercent: number } | null };
  xp: number;
  streakDays: number;
}

function rank(value: unknown) {
  return isObject(value) ? { position: num(value.position), total: num(value.total), topPercent: num(value.top_percent) } : null;
}

/** `learner-progress/{id}/?exam=ielts_general`: bands, CLB, overall band (always an estimate). */
export function parseBandProgress(value: unknown): BandProgress {
  const data = isObject(value) ? value : {};
  const analysis = isObject(data.analysis) ? data.analysis : {};
  const skills = isObject(analysis.skills) ? analysis.skills : {};
  const ranks = isObject(data.rank) ? data.rank : {};
  const verdict = str(analysis.verdict);
  const evolution = Array.isArray(data.evolution) ? (data.evolution as unknown[]).filter(isObject) : [];
  return {
    target: nullable(analysis.target),
    overall: nullable(analysis.overall),
    verdict: verdict === "ready" || verdict === "almost" ? verdict : "not_ready",
    skills: IELTS_SKILLS.map((skill) => {
      const s = isObject(skills[skill]) ? skills[skill] : {};
      return { skill, band: nullable(s.band), clb: nullable(s.clb), gap: nullable(s.gap), trend: str(s.trend) || "unknown" };
    }),
    evolution: evolution.map((row) => {
      const results = isObject(row.skills) ? row.skills : {};
      const out: BandSitting["skills"] = {};
      for (const skill of IELTS_SKILLS) {
        const r = results[skill];
        if (isObject(r)) out[skill] = { band: nullable(r.band), clb: nullable(r.clb ?? r.nclc), cefr: str(r.cefr) };
      }
      return { attempt: str(row.attempt), kind: str(row.kind), mock: nullable(row.mock), at: str(row.at), overall: nullable(row.overall), skills: out };
    }),
    critical: (Array.isArray(analysis.critical) ? (analysis.critical as unknown[]) : []).filter(isObject).map((c) => ({ code: str(c.code), message: str(c.message) })),
    daysLeft: nullable(analysis.days_left),
    rank: { platform: rank(ranks.platform), agency: rank(ranks.agency) },
    xp: num(data.xp),
    streakDays: num(data.streak_days),
  };
}

/** The IELTS target band of a learner (`exam_profiles`), or null. */
export function ieltsTarget(profiles: readonly unknown[] | undefined, exam: string): { target: number | null; center: string; date: string } {
  const row = (profiles ?? []).filter(isObject).find((p) => str(p.exam) === exam);
  return { target: row ? nullable(row.target) : null, center: row ? str(row.exam_center) : "", date: row ? str(row.exam_date) : "" };
}

/** Learners following an exam (`exams`; an empty list means the TCF, as on the server). */
export function followsExam(exams: unknown, exam: string): boolean {
  const rows = Array.isArray(exams) ? (exams as unknown[]).map(str) : [];
  return rows.length ? rows.includes(exam) : exam === "tcf_canada";
}

// --- Official structure of an IELTS mock ----------------------------------------------------

/** « 4 × 10 » for the Listening parts, read from the format table. */
export function listeningShape(sections: unknown): { parts: number; perPart: number } | null {
  const co = isObject(sections) && isObject(sections.co) ? sections.co : null;
  if (!co || !num(co.parts)) return null;
  return { parts: num(co.parts), perPart: num(co.questions_per_part) || Math.round(num(co.questions) / num(co.parts)) };
}
